/* eslint-disable react/prop-types */
/* eslint-disable no-unused-vars */
import React, { useEffect, useRef, useState } from 'react';
import { aggregateData } from '../../utils/dateHelpers';
import { periods } from '../../constants';
import {
  colorsDashboard,
  defaultOptionHighChart
} from '../../constants/defaultOptionsChart';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import PeriodButtons from '../PeriodButtons';
import {
  transformDataToSeries,
  transformSeriesToPercentageStackedArea
} from '../../utils/chartHelper';
import Loading from '../Loading';
import {
  calculateEndDate,
  getSuggestedPeriodByDataRecency,
  isIncompletePeriod
} from '../../utils';

const SplineChart = ({
  data,
  loading,
  categories,
  dateRange,
  isTotal,
  chartTitle = '',
  chartSubtitle = '',
  changePeriod,
  sortingColumnName,
  sendCategoriesSend,
  chartVariant = 'spline'
}) => {
  const [activePeriod, setActivePeriod] = useState(periods.MONTH);
  const periodManualOverrideRef = useRef(false);
  const periodAutoResolvedRef = useRef(false);
  const [options, setOptions] = useState({
    ...defaultOptionHighChart,
    title: {
      text: ''
    },
    subtitle: {
      text: ''
    }
  });

  const drawChart = () => {
    let optionsTemp = transformDataToSeries(
      data,
      categories,
      options,
      sortingColumnName
    );
    if (!optionsTemp) return;

    if (chartVariant === 'percentageStackedArea') {
      optionsTemp = transformSeriesToPercentageStackedArea(optionsTemp);
    }
    const namesArray = optionsTemp?.series
      ?.map(item => item.id)
      .filter(item => item);
    const colorsDashboardTemp = colorsDashboard.slice(1);

    const dates = optionsTemp.xAxis.categories;

    optionsTemp.xAxis.type = 'datetime';
    const referenceDate = dateRange?.endDate || new Date();
    const lastIncompleteIndex =
      dates.length > 0 &&
      isIncompletePeriod(dates[dates.length - 1], activePeriod, referenceDate)
        ? dates.length - 1
        : -1;

    optionsTemp.series = optionsTemp?.series.flatMap((serie, key) => {
      const color = colorsDashboardTemp[key - 1];

      if (!serie?.id) {
        return {
          ...serie,
          dashStyle: 'Solid',
          color,
          keyNumber: key
        };
      }

      if (
        chartVariant === 'percentageStackedArea' ||
        lastIncompleteIndex <= 0
      ) {
        return {
          ...serie,
          dashStyle: 'Solid',
          color,
          keyNumber: key
        };
      }

      const solidData = [...serie.data];
      solidData[lastIncompleteIndex] = null;

      const dashedData = Array(serie.data.length).fill(null);
      dashedData[lastIncompleteIndex - 1] = serie.data[lastIncompleteIndex - 1];
      dashedData[lastIncompleteIndex] = serie.data[lastIncompleteIndex];

      return [
        {
          ...serie,
          data: solidData,
          dashStyle: 'Solid',
          color,
          keyNumber: key
        },
        {
          ...serie,
          id: `${serie.id}-incomplete`,
          linkedTo: serie.id,
          showInLegend: false,
          enableMouseTracking: true,
          data: dashedData,
          dashStyle: 'Dash',
          color,
          keyNumber: key
        }
      ];
    });

    sendCategoriesSend(namesArray);

    setOptions({ ...optionsTemp });
  };

  const handleChangeTime = perTemp => {
    periodManualOverrideRef.current = true;
    periodAutoResolvedRef.current = true;
    changePeriod(perTemp);
    setActivePeriod(perTemp);
  };

  useEffect(() => {
    periodManualOverrideRef.current = false;
    periodAutoResolvedRef.current = false;
  }, [dateRange?.startDate, dateRange?.endDate]);

  useEffect(() => {
    if (
      !data ||
      periodManualOverrideRef.current ||
      periodAutoResolvedRef.current
    )
      return;

    const keyDates = Object.keys(data || {});
    const valueDates = Object.values(data || {}).map(item => item?.date);
    const suggestedPeriod = getSuggestedPeriodByDataRecency([
      ...keyDates,
      ...valueDates
    ]);
    periodAutoResolvedRef.current = true;

    if (suggestedPeriod !== activePeriod) {
      setActivePeriod(suggestedPeriod);
      changePeriod(suggestedPeriod);
    }
  }, [activePeriod, changePeriod, data]);

  useEffect(() => {
    if (!data && options.series.length > 0) {
      setOptions({ ...options, series: [] });
    } else if (data) {
      drawChart();
    }
  }, [activePeriod, data, categories, dateRange]);

  return (
    <div className="col-span-12 min-w-0 overflow-hidden rounded-sm border border-stroke bg-white px-4 pb-5 pt-6 shadow-default dark:border-strokedark dark:bg-boxdark sm:px-6 sm:pt-7.5 xl:col-span-12 xl:px-7.5">
      {loading ? (
        <Loading type="spline" />
      ) : (
        <>
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex w-full flex-wrap gap-3 sm:gap-5">
              <div className="flex w-full">
                <span className="mt-1 mr-2 flex h-4 w-full max-w-4 items-center justify-center rounded-full border border-primary">
                  <span className="block h-2.5 w-full max-w-2.5 rounded-full bg-primary"></span>
                </span>
                <div className="w-full">
                  <p className="break-words font-semibold text-primary">
                    {chartTitle}
                  </p>
                  <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
                    {dateRange?.startDate} - {dateRange?.endDate}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex w-full justify-start lg:max-w-45 lg:justify-end">
              <PeriodButtons
                activePeriod={activePeriod}
                handleChangeTime={handleChangeTime}
              />
            </div>
          </div>
          <div className="mt-6 min-w-0 sm:mt-8">
            <div className="w-full min-w-0 max-w-full overflow-hidden">
              <HighchartsReact
                containerProps={{ className: 'dashboard-chart-host' }}
                highcharts={Highcharts}
                options={options}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SplineChart;
